// VCIN Brand: Always use VCIN_COLORS from constants.js - never use Tailwind blue classes
import React, { useState, useEffect } from 'react';
import { 
  Building2, Plus, Search, ChevronLeft, ChevronRight, X,
  AlertCircle, Loader2, Ban, RotateCcw, Users, MapPin, ChevronDown, Mail, CheckCircle, Clock, Trash2, AlertTriangle, History, Key, Phone
} from 'lucide-react';
import api from '../../services/api';
import { VCIN_COLORS } from '../../styles/constants';

// Status Badge
function StatusBadge({ status }) {
  const config = {
    ACTIVE: { bg: '#E8F5E9', text: '#2E7D32', label: 'Active' },
    SUSPENDED: { bg: '#FFEBEE', text: '#C62828', label: 'Suspended' },
    DELETED: { bg: '#F3F4F6', text: '#6B7280', label: 'Deleted' }
  };
  const style = config[status] || config.ACTIVE;
  
  return (
    <span 
      className="px-2 py-1 rounded text-xs font-semibold"
      style={{ backgroundColor: style.bg, color: style.text }}
    >
      {style.label}
    </span>
  );
}

// Subscription Badge - uses VCIN brand blue for Standard tier
function SubscriptionBadge({ tier }) {
  const config = {
    STANDARD: { bg: `${VCIN_COLORS.primary}15`, text: VCIN_COLORS.primary },
    PROFESSIONAL: { bg: '#E8F5E9', text: '#2E7D32' },
    ENTERPRISE: { bg: '#FFF3E0', text: '#E65100' }
  };
  const style = config[tier] || config.STANDARD;
  
  return (
    <span 
      className="px-2 py-1 rounded text-xs font-semibold"
      style={{ backgroundColor: style.bg, color: style.text }}
    >
      {tier}
    </span>
  );
}

// Table Skeleton
function TableSkeleton() {
  return (
    <tbody className="divide-y divide-gray-100">
      {[...Array(5)].map((_, i) => (
        <tr key={i} className="animate-pulse">
          <td className="px-6 py-4"><div className="h-4 w-40 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-4 w-24 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-5 w-20 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-5 w-16 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-4 w-12 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-4 w-24 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-8 w-24 bg-gray-200 rounded" /></td>
        </tr>
      ))}
    </tbody>
  );
}

// Admin Status Badge
function AdminStatusBadge({ status }) {
  const config = {
    ACTIVE: { bg: '#E8F5E9', text: '#2E7D32', label: 'Active', icon: CheckCircle },
    PENDING: { bg: '#FFF8E1', text: '#E07B00', label: 'Pending Setup', icon: Clock },
    SUSPENDED: { bg: '#FFEBEE', text: '#C62828', label: 'Suspended', icon: Ban }
  };
  const style = config[status] || config.PENDING;
  const Icon = style.icon;
  
  return (
    <span 
      className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold"
      style={{ backgroundColor: style.bg, color: style.text }}
    >
      <Icon className="w-3 h-3" />
      {style.label}
    </span>
  );
}

// Audit Log Item - formats audit actions for display
function AuditLogItem({ log }) {
  // Map action codes to human-readable labels and colors
  const actionConfig = {
    COMPANY_CREATED: { label: 'Company Created', color: '#2E7D32', bg: '#E8F5E9' },
    COMPANY_SUSPENDED: { label: 'Company Suspended', color: '#E07B00', bg: '#FFF8E1' },
    COMPANY_REACTIVATED: { label: 'Company Reactivated', color: '#2E7D32', bg: '#E8F5E9' },
    COMPANY_DELETED: { label: 'Company Deleted', color: '#C62828', bg: '#FFEBEE' },
    USER_CREATED: { label: 'User Created', color: '#1565C0', bg: '#E3F2FD' },
    USER_UPDATED: { label: 'User Updated', color: '#6B7280', bg: '#F3F4F6' },
    OFFICER_CREATED: { label: 'Officer Added', color: '#1565C0', bg: '#E3F2FD' },
    OFFICER_SUSPENDED: { label: 'Officer Suspended', color: '#E07B00', bg: '#FFF8E1' },
    OFFICER_REACTIVATED: { label: 'Officer Reactivated', color: '#2E7D32', bg: '#E8F5E9' },
    SITE_CREATED: { label: 'Site Created', color: '#1565C0', bg: '#E3F2FD' }
  };

  const config = actionConfig[log.action] || { label: log.action, color: '#6B7280', bg: '#F3F4F6' };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="p-3 rounded-lg border border-gray-100" style={{ backgroundColor: config.bg }}>
      <div className="flex items-center justify-between mb-1">
        <span 
          className="text-xs font-semibold px-2 py-0.5 rounded"
          style={{ backgroundColor: config.color + '20', color: config.color }}
        >
          {config.label}
        </span>
        <span className="text-xs text-gray-500">{formatDate(log.timestamp)}</span>
      </div>
      <p className="text-xs text-gray-600">
        By: {log.user_email}
      </p>
      {log.metadata?.action === 'setup_email_resent' && (
        <p className="text-xs text-gray-500 mt-1">Setup email resent</p>
      )}
    </div>
  );
}

// Company Detail Panel
function CompanyDetailPanel({ companyId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState(null);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState(false);
  const [resetPasswordError, setResetPasswordError] = useState(null);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const response = await api.get(`/admin/companies/${companyId}`);
        setData(response.data);
      } catch (err) {
        console.error('Failed to load company detail:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [companyId]);

  const handleResendSetupEmail = async () => {
    setResendLoading(true);
    setResendError(null);
    setResendSuccess(false);
    
    try {
      await api.post(`/admin/companies/${companyId}/resend-setup-email`);
      setResendSuccess(true);
      // Clear success message after 5 seconds
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (err) {
      setResendError(err.message || 'Failed to resend setup email');
    } finally {
      setResendLoading(false);
    }
  };

  const handleResetAdminPassword = async () => {
    setResetPasswordLoading(true);
    setResetPasswordError(null);
    setResetPasswordSuccess(false);
    
    try {
      await api.post(`/admin/companies/${companyId}/reset-admin-password`);
      setResetPasswordSuccess(true);
      // Clear success message after 5 seconds
      setTimeout(() => setResetPasswordSuccess(false), 5000);
    } catch (err) {
      setResetPasswordError(err.message || 'Failed to send password reset email');
    } finally {
      setResetPasswordLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-full max-w-lg bg-white shadow-xl z-50 overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Company Details</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {loading ? (
          <div className="p-6 space-y-4 animate-pulse">
            <div className="h-6 w-48 bg-gray-200 rounded" />
            <div className="h-4 w-32 bg-gray-200 rounded" />
            <div className="h-4 w-40 bg-gray-200 rounded" />
          </div>
        ) : data ? (
          <div className="p-6 space-y-6">
            {/* Company Info */}
            <div>
              <h3 className="text-xl font-bold text-gray-900">{data.company.name}</h3>
              <p className="text-sm text-gray-500 mt-1">{data.company.companies_house_number}</p>
              <div className="flex gap-2 mt-2">
                <StatusBadge status={data.company.status} />
                <SubscriptionBadge tier={data.company.subscription_tier} />
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                <div className="text-sm text-gray-600">
                  {data.company.address || data.company.address_line_1}<br />
                  {data.company.city && data.company.postcode && `${data.company.city}, ${data.company.postcode}`}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">Contact:</span>
                <span className="text-sm text-gray-900">{data.company.contact_email}</span>
              </div>
              {data.company.phone_number && (
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-500 flex items-center gap-1">
                    <Phone className="w-3 h-3" /> Phone:
                  </span>
                  <span className="text-sm text-gray-900">{data.company.phone_number}</span>
                </div>
              )}
            </div>

            {/* Company Admin Section - Always show */}
            <div className="border-t border-gray-100 pt-4">
              <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Company Admin
              </h4>
              {data.company_admin ? (
                <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{data.company_admin.email}</p>
                      <p className="text-xs text-gray-500 mt-0.5">Administrator Account</p>
                    </div>
                    <AdminStatusBadge status={data.company_admin.status} />
                  </div>
                  
                  {data.company_admin.status === 'PENDING' && (
                    <div className="pt-2 border-t border-gray-200">
                      {resendSuccess && (
                        <div className="mb-2 p-2 bg-green-50 border border-green-200 rounded text-sm text-green-700 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          Setup email sent successfully!
                        </div>
                      )}
                      {resendError && (
                        <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-center gap-2">
                          <AlertCircle className="w-4 h-4" />
                          {resendError}
                        </div>
                      )}
                      <button
                        onClick={handleResendSetupEmail}
                        disabled={resendLoading}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                        style={{ backgroundColor: VCIN_COLORS.primary }}
                        data-testid="resend-setup-email-btn"
                      >
                        {resendLoading ? (
                          <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</>
                        ) : (
                          <><Mail className="w-4 h-4" /> Resend Setup Email</>
                        )}
                      </button>
                      <p className="text-xs text-gray-500 mt-2 text-center">
                        This will send a new password setup link to the admin's email
                      </p>
                    </div>
                  )}
                  
                  {(data.company_admin.status === 'ACTIVE' || data.company_admin.status === 'SUSPENDED') && (
                    <div className="pt-2 border-t border-gray-200 space-y-3">
                      {data.company_admin.status === 'ACTIVE' && (
                        <p className="text-xs text-green-600 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          Account is active and ready to use
                        </p>
                      )}
                      {data.company_admin.status === 'SUSPENDED' && (
                        <p className="text-xs text-amber-600 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          Account is suspended
                        </p>
                      )}
                      
                      {resetPasswordSuccess && (
                        <div className="p-2 bg-green-50 border border-green-200 rounded text-sm text-green-700 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          Password reset email sent successfully!
                        </div>
                      )}
                      {resetPasswordError && (
                        <div className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-center gap-2">
                          <AlertCircle className="w-4 h-4" />
                          {resetPasswordError}
                        </div>
                      )}
                      <button
                        onClick={handleResetAdminPassword}
                        disabled={resetPasswordLoading}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 disabled:opacity-50"
                        data-testid="reset-admin-password-btn"
                      >
                        {resetPasswordLoading ? (
                          <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</>
                        ) : (
                          <><Key className="w-4 h-4" /> Reset Password</>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-400">No admin account found</p>
              )}
            </div>

            {/* Officers */}
            <div>
              <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Officers ({data.officers?.length || 0})
              </h4>
              {data.officers?.length === 0 ? (
                <p className="text-sm text-gray-400">No officers</p>
              ) : (
                <div className="space-y-2">
                  {data.officers?.map(officer => (
                    <div key={officer.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{officer.full_name}</p>
                        <p className="text-xs text-gray-500">{officer.email}</p>
                      </div>
                      <StatusBadge status={officer.status} />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Sites */}
            <div>
              <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Sites ({data.sites?.length || 0})
              </h4>
              {data.sites?.length === 0 ? (
                <p className="text-sm text-gray-400">No sites</p>
              ) : (
                <div className="space-y-2">
                  {data.sites?.map(site => (
                    <div key={site.id} className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm font-medium text-gray-900">{site.name}</p>
                      <p className="text-xs text-gray-500">{site.address}, {site.postcode}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Audit History */}
            <div>
              <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <History className="w-4 h-4" />
                Audit History
              </h4>
              {data.audit_history?.length === 0 ? (
                <p className="text-sm text-gray-400">No audit records</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {data.audit_history?.map(log => (
                    <AuditLogItem key={log.id} log={log} />
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500">Failed to load company details</div>
        )}
      </div>
    </>
  );
}

// Create Company Panel
function CreateCompanyPanel({ isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    companies_house_number: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    postcode: '',
    contact_email: '',
    phone_number: '',
    subscription_tier: 'STANDARD'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Company name is required';
    if (!formData.companies_house_number.trim()) newErrors.companies_house_number = 'Companies House number is required';
    if (!formData.address_line_1.trim()) newErrors.address_line_1 = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.postcode.trim()) newErrors.postcode = 'Postcode is required';
    if (!formData.contact_email.trim()) newErrors.contact_email = 'Contact email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) newErrors.contact_email = 'Invalid email format';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Combine address fields into single address string for backend
      const addressParts = [
        formData.address_line_1,
        formData.address_line_2,
        formData.city,
        formData.postcode
      ].filter(Boolean);
      const address = addressParts.join(', ');
      
      // Prepare payload with combined address (created_by is set by backend from auth token)
      const payload = {
        name: formData.name,
        companies_house_number: formData.companies_house_number,
        address: address,
        contact_email: formData.contact_email,
        phone_number: formData.phone_number || null,
        subscription_tier: formData.subscription_tier
      };
      
      await api.post('/admin/companies', payload);
      onSuccess();
      onClose();
      setFormData({
        name: '', companies_house_number: '', address_line_1: '', address_line_2: '',
        city: '', postcode: '', contact_email: '', phone_number: '', subscription_tier: 'STANDARD'
      });
    } catch (err) {
      setError(err.message || 'Failed to create company');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl z-50 overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Create Company</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={`w-full h-10 px-3 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errors.name ? 'border-red-300' : 'border-gray-300'}`}
              data-testid="company-name"
            />
            {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Companies House Number *</label>
            <input
              type="text"
              value={formData.companies_house_number}
              onChange={(e) => setFormData({ ...formData, companies_house_number: e.target.value.toUpperCase() })}
              className={`w-full h-10 px-3 border rounded-lg text-sm font-mono focus:outline-none focus:ring-2 ${errors.companies_house_number ? 'border-red-300' : 'border-gray-300'}`}
              placeholder="12345678"
              data-testid="company-ch-number"
            />
            {errors.companies_house_number && <p className="mt-1 text-xs text-red-600">{errors.companies_house_number}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1 *</label>
            <input
              type="text"
              value={formData.address_line_1}
              onChange={(e) => setFormData({ ...formData, address_line_1: e.target.value })}
              className={`w-full h-10 px-3 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errors.address_line_1 ? 'border-red-300' : 'border-gray-300'}`}
              data-testid="company-address1"
            />
            {errors.address_line_1 && <p className="mt-1 text-xs text-red-600">{errors.address_line_1}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
            <input
              type="text"
              value={formData.address_line_2}
              onChange={(e) => setFormData({ ...formData, address_line_2: e.target.value })}
              className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className={`w-full h-10 px-3 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errors.city ? 'border-red-300' : 'border-gray-300'}`}
                data-testid="company-city"
              />
              {errors.city && <p className="mt-1 text-xs text-red-600">{errors.city}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Postcode *</label>
              <input
                type="text"
                value={formData.postcode}
                onChange={(e) => setFormData({ ...formData, postcode: e.target.value.toUpperCase() })}
                className={`w-full h-10 px-3 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errors.postcode ? 'border-red-300' : 'border-gray-300'}`}
                placeholder="SW1A 1AA"
                data-testid="company-postcode"
              />
              {errors.postcode && <p className="mt-1 text-xs text-red-600">{errors.postcode}</p>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email *</label>
            <input
              type="email"
              value={formData.contact_email}
              onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
              className={`w-full h-10 px-3 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errors.contact_email ? 'border-red-300' : 'border-gray-300'}`}
              placeholder="contact@company.co.uk"
              data-testid="company-email"
            />
            {errors.contact_email && <p className="mt-1 text-xs text-red-600">{errors.contact_email}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
            <input
              type="tel"
              value={formData.phone_number}
              onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
              className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
              placeholder="020 1234 5678"
              data-testid="company-phone"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subscription Tier</label>
            <select
              value={formData.subscription_tier}
              onChange={(e) => setFormData({ ...formData, subscription_tier: e.target.value })}
              className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
              data-testid="company-tier"
            >
              <option value="STANDARD">Standard</option>
              <option value="PROFESSIONAL">Professional</option>
              <option value="ENTERPRISE">Enterprise</option>
            </select>
          </div>

          <div className="pt-4 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 h-11 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 h-11 rounded-lg font-medium text-white flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ backgroundColor: VCIN_COLORS.primary }}
              data-testid="submit-company-btn"
            >
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating...</> : 'Create Company'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

// Delete Confirmation Dialog
function DeleteConfirmationDialog({ company, isOpen, onClose, onConfirm, loading }) {
  if (!isOpen || !company) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white rounded-xl shadow-2xl z-50 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Delete Company</h3>
        </div>
        
        <p className="text-sm text-gray-600 mb-4">
          Are you sure you want to delete <strong>{company.name}</strong>? This will deactivate all associated users and sites. All sighting and vehicle data submitted by officers will be retained for audit purposes. This action cannot be undone.
        </p>
        
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 h-11 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            data-testid="cancel-delete-btn"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 h-11 rounded-lg font-medium text-white bg-red-600 hover:bg-red-700 flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="confirm-delete-btn"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Deleting...</>
            ) : (
              <><Trash2 className="w-4 h-4" /> Delete Company</>
            )}
          </button>
        </div>
      </div>
    </>
  );
}

export default function SACompaniesPage() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchCompanies = async (page = 1, includeDeleted = showDeleted) => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/companies?page=${page}&limit=20&include_deleted=${includeDeleted}`);
      setCompanies(response.data.companies);
      setPagination(response.data.pagination);
    } catch (err) {
      setError(err.message || 'Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies(1, showDeleted);
  }, [showDeleted]);

  const handleStatusChange = async (companyId, action) => {
    try {
      setActionLoading(companyId);
      await api.patch(`/admin/companies/${companyId}/${action}`);
      await fetchCompanies(pagination.page, showDeleted);
    } catch (err) {
      alert(err.message || `Failed to ${action} company`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteCompany = async () => {
    if (!deleteTarget) return;
    
    try {
      setDeleteLoading(true);
      await api.delete(`/admin/companies/${deleteTarget.id}`);
      setDeleteTarget(null);
      await fetchCompanies(pagination.page, showDeleted);
    } catch (err) {
      alert(err.message || 'Failed to delete company');
    } finally {
      setDeleteLoading(false);
    }
  };

  const filteredCompanies = companies.filter(company =>
    company.name?.toLowerCase().includes(search.toLowerCase()) ||
    company.companies_house_number?.toLowerCase().includes(search.toLowerCase())
  );

  if (error && !loading) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-red-600" />
        <span className="text-red-700">{error}</span>
      </div>
    );
  }

  return (
    <div data-testid="sa-companies-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Companies</h1>
        <button
          onClick={() => setShowCreatePanel(true)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-white hover:opacity-90"
          style={{ backgroundColor: VCIN_COLORS.primary }}
          data-testid="create-company-btn"
        >
          <Plus className="w-4 h-4" />
          Create Company
        </button>
      </div>

      {/* Search and Filter */}
      <div className="mb-6 flex items-center justify-between">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or Companies House number..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-10 pl-10 pr-4 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
            data-testid="company-search"
          />
        </div>
        <label className="flex items-center gap-2 ml-4 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showDeleted}
            onChange={(e) => setShowDeleted(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-gray-600 focus:ring-gray-500"
            data-testid="show-deleted-toggle"
          />
          <span className="text-sm text-gray-600">Show Deleted</span>
        </label>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="companies-table">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Company Name</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">CH Number</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Subscription</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Officers</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Created</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            
            {loading ? (
              <TableSkeleton />
            ) : filteredCompanies.length === 0 ? (
              <tbody>
                <tr>
                  <td colSpan="7" className="p-12 text-center">
                    <Building2 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="font-medium text-gray-600">No companies found</p>
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody className="divide-y divide-gray-100">
                {filteredCompanies.map((company) => (
                  <tr 
                    key={company.id} 
                    className={`hover:bg-gray-50 cursor-pointer ${
                      company.status === 'DELETED' ? 'bg-gray-50/50 opacity-60' : 
                      company.status === 'SUSPENDED' ? 'bg-red-50/30' : ''
                    }`}
                    onClick={() => setSelectedCompanyId(company.id)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{company.name}</span>
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-mono text-sm text-gray-600">{company.companies_house_number}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <SubscriptionBadge tier={company.subscription_tier} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={company.status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1.5 text-sm text-gray-600">
                        <Users className="w-4 h-4" />
                        {company.officer_count}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(company.created_at).toLocaleDateString('en-GB', {
                        day: '2-digit', month: 'short', year: 'numeric'
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {company.status === 'DELETED' ? (
                        <span className="text-sm text-gray-400 italic">Deleted</span>
                      ) : (
                        <div className="flex items-center gap-2">
                          {company.status === 'ACTIVE' ? (
                            <button
                              onClick={() => handleStatusChange(company.id, 'suspend')}
                              disabled={actionLoading === company.id}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-amber-600 bg-amber-50 rounded-lg hover:bg-amber-100 disabled:opacity-50"
                              data-testid={`suspend-company-${company.id}`}
                            >
                              {actionLoading === company.id ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Ban className="w-3.5 h-3.5" />
                              )}
                              Suspend
                            </button>
                          ) : (
                            <button
                              onClick={() => handleStatusChange(company.id, 'reactivate')}
                              disabled={actionLoading === company.id}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-green-600 bg-green-50 rounded-lg hover:bg-green-100 disabled:opacity-50"
                              data-testid={`reactivate-company-${company.id}`}
                            >
                              {actionLoading === company.id ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <RotateCcw className="w-3.5 h-3.5" />
                              )}
                              Reactivate
                            </button>
                          )}
                          <button
                            onClick={() => setDeleteTarget(company)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100"
                            data-testid={`delete-company-${company.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
            <span className="text-sm text-gray-500">
              Showing {(pagination.page - 1) * 20 + 1} to {Math.min(pagination.page * 20, pagination.total)} of {pagination.total}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchCompanies(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600 px-2">Page {pagination.page} of {pagination.pages}</span>
              <button
                onClick={() => fetchCompanies(pagination.page + 1)}
                disabled={pagination.page === pagination.pages}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Company Panel */}
      <CreateCompanyPanel
        isOpen={showCreatePanel}
        onClose={() => setShowCreatePanel(false)}
        onSuccess={() => fetchCompanies(1, showDeleted)}
      />

      {/* Company Detail Panel */}
      {selectedCompanyId && (
        <CompanyDetailPanel
          companyId={selectedCompanyId}
          onClose={() => setSelectedCompanyId(null)}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        company={deleteTarget}
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteCompany}
        loading={deleteLoading}
      />
    </div>
  );
}
